import { useState, useCallback, useEffect, useRef } from 'react'
import { Chessboard } from 'react-chessboard'
import MoveHistory from './MoveHistory.jsx'
import PromotionModal from './PromotionModal.jsx'
import TurnIndicator from './TurnIndicator.jsx'
import { boardThemes, DEFAULT_BOARD_THEME } from '@/themes/boardThemes.js'
import { getPieceTheme } from '@/themes/pieceThemes.jsx'

export default function ChessPanel({
  fen,
  moveHistory,
  lastMove,
  promotionPending,
  syncing,
  currentTurn,
  isMyTurn,
  isCheck,
  playerColor,
  gameActive,
  makeMove,
  handlePromotion,
  cancelPromotion,
  isCreator,
  guestJoined,
  getLegalMovesFrom,
  getCurrentGame,
  boardThemeName = DEFAULT_BOARD_THEME,
  pieceThemeName = 'default',
}) {
  const theme = boardThemes[boardThemeName] ?? boardThemes[DEFAULT_BOARD_THEME]
  const customPieces = getPieceTheme(pieceThemeName)

  const [optionSquares, setOptionSquares] = useState({})
  const [selectedSquare, setSelectedSquare] = useState(null)

  // Measure the container for an explicit boardWidth (fixes board collapse on mobile)
  const boardContainerRef = useRef(null)
  const [boardWidth, setBoardWidth] = useState(480)

  useEffect(() => {
    const el = boardContainerRef.current
    if (!el) return
    const measure = () => {
      const w = el.clientWidth - 24 // subtract p-3 padding on each side
      setBoardWidth(Math.max(200, w))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Clear selection when it's no longer our turn (e.g. after making a move)
  useEffect(() => {
    if (!isMyTurn) {
      setSelectedSquare(null)
      setOptionSquares({})
    }
  }, [isMyTurn])

  // ─── square styles ─────────────────────────────────────────────────────────

  const customSquareStyles = {}

  if (lastMove) {
    customSquareStyles[lastMove.from] = { backgroundColor: theme.highlightFrom }
    customSquareStyles[lastMove.to] = { backgroundColor: theme.highlightTo }
  }

  Object.entries(optionSquares).forEach(([sq, style]) => {
    customSquareStyles[sq] = { ...customSquareStyles[sq], ...style }
  })

  // ─── legal move display ────────────────────────────────────────────────────

  function computeLegalMoveStyles(square) {
    const moves = getLegalMovesFrom ? getLegalMovesFrom(square) : []
    const styles = {
      [square]: { backgroundColor: theme.selectedSquare },
    }

    // Get current game state to distinguish captures
    const game = getCurrentGame ? getCurrentGame() : null

    moves.forEach((m) => {
      const hasCapture = game?.get(m.to)
      styles[m.to] = hasCapture
        ? {
            background: `radial-gradient(circle, ${theme.legalCapture} 60%, ${theme.legalCapture.replace(/[\d.]+\)$/, '0.55)')} 60%)`,
            borderRadius: '50%',
          }
        : {
            background: `radial-gradient(circle, ${theme.legalDot} 25%, transparent 26%)`,
            borderRadius: '50%',
          }
    })
    return { styles, targets: moves.map((m) => m.to) }
  }

  // ─── interaction ──────────────────────────────────────────────────────────

  async function onSquareClick(square) {
    if (!isMyTurn || !gameActive) return

    if (selectedSquare) {
      const from = selectedSquare

      if (from === square) {
        setSelectedSquare(null)
        setOptionSquares({})
        return
      }

      const { targets } = computeLegalMoveStyles(from)

      if (targets.includes(square)) {
        setSelectedSquare(null)
        setOptionSquares({})
        await makeMove(from, square)
        return
      }

      // Maybe they clicked a different friendly piece
      const game = getCurrentGame ? getCurrentGame() : null
      const piece = game?.get(square)
      const myChar = playerColor === 'white' ? 'w' : 'b'
      if (piece && piece.color === myChar) {
        setSelectedSquare(square)
        const { styles } = computeLegalMoveStyles(square)
        setOptionSquares(styles)
      } else {
        setSelectedSquare(null)
        setOptionSquares({})
      }
      return
    }

    // No piece selected yet
    const game = getCurrentGame ? getCurrentGame() : null
    const piece = game?.get(square)
    const myChar = playerColor === 'white' ? 'w' : 'b'
    if (!piece || piece.color !== myChar) return

    setSelectedSquare(square)
    const { styles } = computeLegalMoveStyles(square)
    setOptionSquares(styles)
  }

  async function onPieceDrop(sourceSquare, targetSquare) {
    if (!isMyTurn || !gameActive) return false
    setSelectedSquare(null)
    setOptionSquares({})
    const result = await makeMove(sourceSquare, targetSquare)
    return result === true
  }

  function isDraggablePiece({ piece }) {
    if (!isMyTurn || !gameActive) return false
    const myChar = playerColor === 'white' ? 'w' : 'b'
    return piece[0] === myChar
  }

  // ─── render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-4 h-full">
      <TurnIndicator
        currentTurn={currentTurn}
        isMyTurn={isMyTurn}
        isCheck={isCheck}
        playerColor={playerColor}
        gameActive={gameActive}
        guestJoined={guestJoined}
        isCreator={isCreator}
      />

      <div className="flex flex-col xl:flex-row gap-6 items-start">
        {/* Board */}
        <div className="card overflow-hidden flex-shrink-0 w-full xl:w-[65%] max-w-[800px] chess-board-wrapper relative">
          {/* Overlay when game not active */}
          {!gameActive && (
            <div className="absolute inset-0 bg-obsidian-950/70 z-10 flex items-center justify-center rounded-2xl backdrop-blur-sm">
              <div className="text-center">
                <div className="text-4xl mb-3 opacity-30">♟</div>
                <p className="text-ivory-300/40 text-sm">
                  {!guestJoined
                    ? 'Waiting for opponent…'
                    : isCreator
                    ? 'Click "Start Chess Match" to begin'
                    : 'Waiting for host to start…'}
                </p>
              </div>
            </div>
          )}

          <div className="relative p-3" ref={boardContainerRef}>
            <Chessboard
              id="DueloBoard"
              position={fen}
              boardWidth={boardWidth}
              onSquareClick={onSquareClick}
              onPieceDrop={onPieceDrop}
              isDraggablePiece={isDraggablePiece}
              boardOrientation={playerColor === 'white' ? 'white' : 'black'}
              customSquareStyles={customSquareStyles}
              arePiecesDraggable={gameActive && isMyTurn && !syncing}
              areArrowsAllowed
              customDarkSquareStyle={{ backgroundColor: theme.darkSquare }}
              customLightSquareStyle={{ backgroundColor: theme.lightSquare }}
              customBoardStyle={{
                borderRadius: '8px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              }}
              {...(customPieces ? { customPieces } : {})}
            />

            {/* Syncing spinner */}
            {syncing && (
              <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-obsidian-800/90 flex items-center justify-center shadow">
                <svg className="w-3.5 h-3.5 animate-spin text-gold-400" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Move history */}
        <div className="card flex-1 min-h-[200px] xl:min-h-[400px] flex flex-col overflow-hidden w-full">
          <div className="px-4 py-3 border-b border-obsidian-600">
            <h3 className="text-xs font-mono uppercase tracking-widest text-ivory-300/50">Move History</h3>
          </div>
          <MoveHistory moves={moveHistory} />
        </div>
      </div>

      {promotionPending && (
        <PromotionModal
          playerColor={playerColor}
          onSelect={handlePromotion}
          onCancel={cancelPromotion}
        />
      )}
    </div>
  )
}
